/**
 * Organization Settings Section
 *
 * Displays organization details and allows management (for owners).
 */

import { useState } from "react";
import {
  AlertTriangle,
  Building2,
  Users,
  Mail,
  Calendar,
  Pencil,
  Plus,
  Loader2,
  X,
  MessageSquare,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useOrganizationContext } from "@/providers/OrganizationProvider";
import { PendingInvitesList } from "@/components/organization/PendingInvitesList";
import {
  useUpdateOrganization,
  useCreateOrganizationInvite,
  useOrganizationInvites,
  useCancelOrganizationInvite,
  useOrganizationMembers,
  useRemoveOrganizationMember,
} from "@/hooks/useOrganization";
import type { InviteStatus, OrganizationMember } from "@/types/organization";

export function OrganizationSection() {
  const { organization, isOrganizationOwner, pendingInvites, refetchOrganization } =
    useOrganizationContext();
  const updateOrgMutation = useUpdateOrganization();
  const createInviteMutation = useCreateOrganizationInvite();
  const { data: orgInvites, isLoading: invitesLoading } = useOrganizationInvites(
    isOrganizationOwner ? organization?.id : undefined
  );
  const cancelInviteMutation = useCancelOrganizationInvite();
  const { data: members, isLoading: membersLoading } = useOrganizationMembers(organization?.id);
  const removeemberMutation = useRemoveOrganizationMember();

  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState("");
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [memberToRemove, setMemberToRemove] = useState<OrganizationMember | null>(null);
  const [emailWarning, setEmailWarning] = useState<string | null>(null);
  const [isEditingTeamsWebhook, setIsEditingTeamsWebhook] = useState(false);
  const [teamsWebhookUrl, setTeamsWebhookUrl] = useState("");

  const getStatusBadgeVariant = (status: InviteStatus) => {
    switch (status) {
      case "PENDING":
        return "outline" as const;
      case "ACCEPTED":
        return "default" as const;
      case "DECLINED":
        return "destructive" as const;
      case "EXPIRED":
        return "secondary" as const;
      default:
        return "outline" as const;
    }
  };

  if (!organization) {
    return (
      <div className="space-y-6">
        {/* Pending invitations (for users who belong to an org but also have invites to join another) */}
        {pendingInvites.length > 0 && (
          <Card className="rounded-[15px] shadow-none">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Pending Invitations
              </CardTitle>
              <CardDescription>
                You&apos;ve been invited to join other organizations. Accept to switch.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PendingInvitesList />
            </CardContent>
          </Card>
        )}
        <Card className="rounded-[15px] shadow-none">
          <CardContent className="pt-6">
            <p className="text-muted-foreground">No organization found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const handleEditName = () => {
    setNewName(organization.name);
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    if (!newName.trim() || newName.trim() === organization.name) {
      setIsEditingName(false);
      return;
    }

    try {
      await updateOrgMutation.mutateAsync({
        orgId: organization.id,
        data: { name: newName.trim() },
      });
      refetchOrganization();
      setIsEditingName(false);
    } catch (error) {
      console.error("Failed to update organization name:", error);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
    setNewName("");
  };

  const handleEditTeamsWebhook = () => {
    setTeamsWebhookUrl("");
    setIsEditingTeamsWebhook(true);
  };

  const handleSaveTeamsWebhook = async () => {
    if (!organization) return;

    try {
      await updateOrgMutation.mutateAsync({
        orgId: organization.id,
        data: { teams_webhook_url: teamsWebhookUrl.trim() || null },
      });
      refetchOrganization();
      setIsEditingTeamsWebhook(false);
    } catch (error) {
      console.error("Failed to update Teams webhook URL:", error);
    }
  };

  const handleCancelTeamsWebhookEdit = () => {
    setIsEditingTeamsWebhook(false);
    setTeamsWebhookUrl("");
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setEmailWarning(null);

    try {
      const result = await createInviteMutation.mutateAsync({
        orgId: organization.id,
        data: { email: inviteEmail.trim() },
      });
      setInviteEmail("");
      setShowInviteDialog(false);
      if (result?.email_sent === false) {
        setEmailWarning(
          `Invite created for ${result.email}, but the notification email could not be delivered. The user can still sign up and accept the invite from their settings page.`
        );
      }
    } catch (error) {
      console.error("Failed to send invite:", error);
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove || !organization) return;

    try {
      await removeemberMutation.mutateAsync({
        orgId: organization.id,
        userId: memberToRemove.id,
      });
      setMemberToRemove(null);
    } catch (error) {
      console.error("Failed to remove member:", error);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="space-y-6">
      {/* Pending invitations (invites to join other orgs) */}
      {pendingInvites.length > 0 && (
        <Card className="rounded-[15px] shadow-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Pending Invitations
            </CardTitle>
            <CardDescription>
              You&apos;ve been invited to join other organizations. Accept to switch.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PendingInvitesList />
          </CardContent>
        </Card>
      )}

      {/* Organization Details */}
      <Card className="rounded-[15px] shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Organization Details
          </CardTitle>
          <CardDescription>
            {isOrganizationOwner
              ? "Manage your organization settings"
              : "View your organization details"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Organization Name */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-muted-foreground">Organization Name</Label>
            {isEditingName ? (
              <div className="flex items-center gap-2">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="max-w-sm"
                  autoFocus
                />
                <Button size="sm" onClick={handleSaveName} disabled={updateOrgMutation.isPending}>
                  {updateOrgMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Save"
                  )}
                </Button>
                <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <p className="text-base font-medium">{organization.name}</p>
                {isOrganizationOwner && (
                  <Button size="sm" variant="ghost" onClick={handleEditName}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                )}
              </div>
            )}
          </div>

          <Separator />

          {/* Microsoft Teams Webhook Configuration */}
          {isOrganizationOwner && (
            <>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Microsoft Teams Webhook URL
                </Label>
                {isEditingTeamsWebhook ? (
                  <div className="space-y-2">
                    <Input
                      value={teamsWebhookUrl}
                      onChange={(e) => setTeamsWebhookUrl(e.target.value)}
                      placeholder="https://your-domain.webhook.office.com/webhookb2/..."
                      className="max-w-2xl"
                      autoFocus
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={handleSaveTeamsWebhook}
                        disabled={updateOrgMutation.isPending}
                      >
                        {updateOrgMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Save"
                        )}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={handleCancelTeamsWebhookEdit}>
                        Cancel
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Configure a Power Automate webhook URL to receive alert notifications in
                      Microsoft Teams.
                      <a
                        href="https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-1 text-primary hover:underline"
                      >
                        Learn more
                      </a>
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-base font-medium">
                      {organization.teams_webhook_url_last_four ? (
                        <span className="font-mono text-sm text-muted-foreground">
                          ****{organization.teams_webhook_url_last_four}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Not configured</span>
                      )}
                    </p>
                    <Button size="sm" variant="ghost" onClick={handleEditTeamsWebhook}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
              <Separator />
            </>
          )}

          {/* Organization Info Grid */}
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Created
              </div>
              <p className="text-base font-medium">{formatDate(organization.created_at)}</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Users className="h-4 w-4" />
                Your Role
              </div>
              <Badge variant={isOrganizationOwner ? "default" : "secondary"}>
                {isOrganizationOwner ? "Owner" : "Member"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invite Members (Owner only) */}
      {isOrganizationOwner && (
        <Card className="rounded-[15px] shadow-none">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Invitations
                </CardTitle>
                <CardDescription>Manage invitations to your organization</CardDescription>
              </div>
              <Button onClick={() => setShowInviteDialog(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Invite
              </Button>
            </div>
            {emailWarning && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="space-y-1">
                  <p>{emailWarning}</p>
                  <button
                    className="text-xs underline hover:no-underline"
                    onClick={() => setEmailWarning(null)}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {invitesLoading ? (
              <p className="text-sm text-muted-foreground">Loading invites...</p>
            ) : orgInvites && orgInvites.length > 0 ? (
              <div className="space-y-3">
                {orgInvites.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between rounded-[10px] border p-3"
                  >
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{invite.email}</p>
                      <p className="text-xs text-muted-foreground">
                        Sent {formatDate(invite.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="capitalize" variant={getStatusBadgeVariant(invite.status)}>
                        {invite.status}
                      </Badge>
                      {invite.status === "PENDING" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() =>
                            cancelInviteMutation.mutate({
                              orgId: organization.id,
                              inviteId: invite.id,
                            })
                          }
                          disabled={cancelInviteMutation.isPending}
                          title="Cancel invite"
                        >
                          {cancelInviteMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No invitations sent yet. Invite team members to join your organization.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Members */}
      <Card className="rounded-[15px] shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Members
          </CardTitle>
          <CardDescription>
            {members?.length || 0} member{members?.length !== 1 ? "s" : ""} in your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          {membersLoading ? (
            <p className="text-sm text-muted-foreground">Loading members...</p>
          ) : members && members.length > 0 ? (
            <div className="space-y-3">
              {members.map((member) => {
                const isOwner = member.id === organization?.owner_user_id;
                return (
                  <div
                    key={member.id}
                    className="flex items-center justify-between rounded-[10px] border p-3"
                  >
                    <div className="flex items-center gap-3">
                      {member.profile_image_url &&
                      !member.profile_image_url.includes("profileimageurl.com") ? (
                        <img
                          src={member.profile_image_url}
                          alt={member.name}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
                          {getInitials(member.name)}
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium">{member.name}</p>
                        <p className="text-xs text-muted-foreground">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={isOwner ? "default" : "secondary"}>
                        {isOwner ? "Owner" : "Member"}
                      </Badge>
                      {isOrganizationOwner && !isOwner && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => setMemberToRemove(member)}
                          title="Remove member"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No members found.</p>
          )}
        </CardContent>
      </Card>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite Member</DialogTitle>
            <DialogDescription>Send an invitation to join your organization.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email Address</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="colleague@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowInviteDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createInviteMutation.isPending}>
                {createInviteMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Invitation"
                )}
              </Button>
            </DialogFooter>
            {createInviteMutation.isError && (
              <p className="mt-2 text-sm text-red-600">
                {createInviteMutation.error instanceof Error
                  ? createInviteMutation.error.message
                  : "Failed to send invitation"}
              </p>
            )}
          </form>
        </DialogContent>
      </Dialog>

      {/* Remove Member Confirmation Dialog */}
      <Dialog open={!!memberToRemove} onOpenChange={() => setMemberToRemove(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove{" "}
              <span className="font-medium">{memberToRemove?.name}</span> from the organization?
              They will lose access to all organization resources.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMemberToRemove(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveMember}
              disabled={removeemberMutation.isPending}
            >
              {removeemberMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removing...
                </>
              ) : (
                "Remove"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
